import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Drawer,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  OutlinedInput,
  Chip,
  Checkbox,
  ListItemText,
  List,
  ListItem,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import MapIcon from '@mui/icons-material/Map';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FilterListIcon from '@mui/icons-material/FilterList';
import LayersIcon from '@mui/icons-material/Layers';
import CloseIcon from '@mui/icons-material/Close';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import MapView from '../components/MapView';
import { MapSearch } from '../components/MapSearch';
import { CPEFormDialog } from '../components/CPEFormDialog';
import { CPEDetailsDialog } from '../components/CPEDetailsDialog';
import { EnergyDecor } from '../components/EnergyDecor';
import { animations, transitions } from '../theme/animations';
import { cpeService } from '../services/cpeService';
import zoneService from '../services/zoneService';
import nextActionOptionService from '../services/nextActionOptionService';
import { userService } from '../services/userService';
import { CPE, CPEFilters, PLANT_TYPES, CPE_STATUSES, COMMERCIAL_STATUSES, PlantType, CPEStatus, CommercialStatus, InternalPositioning, NextActionOption } from '../types/cpe';
import { Zone } from '../types/zone';
import { User } from '../types/user';
import { useMapFilters } from '../hooks/useMapFilters';

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [cpes, setCPEs] = useState<CPE[]>([]);
  const [loadingCPEs, setLoadingCPEs] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formInitialLat, setFormInitialLat] = useState<string>();
  const [formInitialLng, setFormInitialLng] = useState<string>();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Use persistent filter hook
  const {
    filters,
    setFilters,
    visibleZones,
    setVisibleZones,
    mapDarkMode,
    setMapDarkMode,
    clearFilters,
    areZonesInitialized,
  } = useMapFilters();

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedCPEId, setSelectedCPEId] = useState<string | null>(null);
  const [zoneDrawerOpen, setZoneDrawerOpen] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [nextActionOptions, setNextActionOptions] = useState<NextActionOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  // Map control for search functionality
  const mapControlRef = React.useRef<{
    navigateTo: (lat: number, lng: number, zoom?: number) => void;
    selectCPE?: (cpeId: string) => void;
    deselectCPE?: () => void;
  }>({
    navigateTo: () => {},
  });

  const canAddCPE = user?.role === 'admin' || user?.role === 'super_admin';

  // Handle navigation from coordinates click
  useEffect(() => {
    const state = location.state as { centerLat?: number; centerLng?: number; zoom?: number; cpeId?: string } | null;
    if (state?.centerLat && state?.centerLng && mapControlRef.current.navigateTo) {
      // Small delay to ensure map is ready
      setTimeout(() => {
        // Navigate to location with zoom level 7
        mapControlRef.current.navigateTo(state.centerLat!, state.centerLng!, state.zoom || 10);
        // If CPE ID is provided, select it to show tooltip and enlarge marker
        // But DON'T open the details dialog
        if (state.cpeId) {
          setSelectedCPEId(state.cpeId);
        }
      }, 500);
      // Clear the state to prevent re-triggering
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Check if user must change password
  useEffect(() => {
    if (user?.must_change_password) {
      navigate('/change-password', { state: { isRequired: true } });
    }
  }, [user, navigate]);

  // Load CPEs for map with filters
  const loadCPEs = useCallback(async () => {
    try {
      // Only show loading on initial load, not on filter changes
      if (cpes.length === 0) {
        setLoadingCPEs(true);
      }
      setError(null);
      const response = await cpeService.getCPEs(1, 10000, filters); // Load all CPEs with filters
      setCPEs(response.data.cpes);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string; errors?: Record<string, string> }; status?: number } };
      console.error('Failed to load CPEs:', err);
      setError(error.response?.data?.message || 'Failed to load CPEs');
    } finally {
      setLoadingCPEs(false);
    }
  }, [filters, cpes.length]);

  useEffect(() => {
    loadCPEs();
  }, [loadCPEs]);

  // Load zones for visibility toggles
  const loadZones = useCallback(async () => {
    try {
      const zonesList = await zoneService.getZonesList();
      setZones(zonesList);
      // Only initialize visible zones on first visit (not when user has already configured)
      if (!areZonesInitialized()) {
        const visibleIds = new Set(zonesList.filter(z => z.is_visible).map(z => z.id));
        setVisibleZones(visibleIds);
      }
    } catch (error) {
      console.error('Error loading zones:', error);
    }
  }, [areZonesInitialized, setVisibleZones]);

  useEffect(() => {
    loadZones();
  }, [loadZones]);

  // Load users for filters
  const loadUsers = useCallback(async () => {
    try {
      const usersList = await userService.getAllUsers();
      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load next action options for filter
  useEffect(() => {
    nextActionOptionService.getAll()
      .then(setNextActionOptions)
      .catch((error) => console.error('Error loading next action options:', error));
  }, []);

  const handleCPEClick = (cpe: CPE) => {
    setSelectedCPEId(cpe.id);
    setDetailsDialogOpen(true);
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setFormInitialLat(undefined);
    setFormInitialLng(undefined);
    loadCPEs(); // Reload map data
  };

  const handleFilterChange = (field: keyof CPEFilters, value: string | number | boolean | PlantType[] | CPEStatus[] | CommercialStatus[] | InternalPositioning[] | string[] | undefined) => {
    // Preserve explicit boolean false and number 0 as valid filter values
    const normalizedValue = value === undefined || value === '' || (Array.isArray(value) && value.length === 0) ? undefined : value;
    setFilters({ ...filters, [field]: normalizedValue });
  };


  const toggleZoneVisibility = (zoneId: string) => {
    const newVisibleZones = new Set(visibleZones);
    if (newVisibleZones.has(zoneId)) {
      newVisibleZones.delete(zoneId);
    } else {
      newVisibleZones.add(zoneId);
    }
    setVisibleZones(newVisibleZones);
  };

  const showAllZones = () => {
    const allZoneIds = new Set(zones.map(z => z.id));
    setVisibleZones(allZoneIds);
  };

  const hideAllZones = () => {
    setVisibleZones(new Set());
  };

  // Calculate stats
  const totalCPEs = cpes.length;
  const waldevarCPEsCount = cpes.filter(cpe => cpe.is_waldevar_pp).length;

  // Contacted CPEs: commercial status is contacted, offered, contracted, or lost
  const contactedCPEs = cpes.filter(cpe =>
    cpe.commercial_status && ['Contacted', 'O&M Offered', 'O&M Contracted', 'Lost'].includes(cpe.commercial_status)
  ).length;

  // Overdue Next Actions: has an action set AND due date is in the past
  const today = new Date().toISOString().split('T')[0];
  const overdueNextActions = cpes.filter(cpe =>
    cpe.next_action_id != null && cpe.next_action_due_date != null && cpe.next_action_due_date < today
  ).length;

  return (
    <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ my: 4 }}>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                transition: transitions.default,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BusinessIcon color="secondary" />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.totalCpes', 'Total CPEs')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" color="secondary">
                    {totalCPEs}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                transition: transitions.default,
                position: 'relative',
                overflow: 'visible',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EnergyDecor variant="sun" size={24} animated />
                    </Box>
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.waldevarCpes', 'Waldevar CPEs')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: '#1d388d' }}>
                    {waldevarCPEsCount}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{
                transition: transitions.default,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6,
                },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUpIcon color="warning" />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.contactedCpes', 'Contacted CPEs')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" color="warning.main">
                    {contactedCPEs}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card
                onClick={() => navigate('/cpes', { state: { filters: { overdue_next_action: true } } })}
                sx={{
                  cursor: 'pointer',
                  transition: transitions.default,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccessAlarmIcon color={overdueNextActions > 0 ? 'error' : 'disabled'} />
                    <Typography variant="h6" color="text.secondary">
                      {t('dashboard.overdueNextActions', 'Overdue Next Actions')}
                    </Typography>
                  </Box>
                  <Typography variant="h3" color={overdueNextActions > 0 ? 'error.main' : 'text.secondary'}>
                    {overdueNextActions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Map View Section - Full Width */}
          <Card sx={{
            animation: animations.fadeIn,
            animationDelay: '0.5s',
            transition: transitions.default,
            '&:hover': {
              boxShadow: 4,
            },
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <MapIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5">{t('dashboard.mapOverview', 'CPE Map Overview')}</Typography>
                </Box>
            </Box>

            {loadingCPEs && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 600 }}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!loadingCPEs && !error && (
              <Box sx={{ height: 600, width: '100%', borderRadius: 1, overflow: 'hidden', position: 'relative' }}>
                {/* Search Button - Top Right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 1500, // Higher than other buttons to allow dropdown to appear on top
                  }}
                >
                  <MapSearch
                    cpes={cpes}
                    onLocationSelect={(lat, lng) => {
                      mapControlRef.current.navigateTo(lat, lng, 10);
                    }}
                    onPPSelect={(ppId, lat, lng) => {
                      // Navigate to the PP location
                      mapControlRef.current.navigateTo(lat, lng, 10);
                      // Select the PP to enlarge pin and show tooltip
                      setSelectedCPEId(ppId);
                    }}
                  />
                </Box>

                {/* Fullscreen Button - Below Search */}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 60,
                    right: 10,
                    zIndex: 1000,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    color: 'text.primary',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
                    },
                  }}
                  onClick={() => navigate('/map')}
                  title="Full Screen Map"
                >
                  <FullscreenIcon />
                </IconButton>

                {/* Filter Button - Below Fullscreen */}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 110,
                    right: 10,
                    zIndex: 1000,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    color: 'text.primary',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
                    },
                  }}
                  onClick={() => setFilterDrawerOpen(true)}
                  title="Filter CPEs"
                >
                  <FilterListIcon />
                </IconButton>

                {/* Map Theme Toggle Button - Below Filter */}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 160,
                    right: 10,
                    zIndex: 1000,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    color: 'text.primary',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
                    },
                  }}
                  onClick={() => setMapDarkMode(!mapDarkMode)}
                  title={mapDarkMode ? "Light Map Theme" : "Dark Map Theme"}
                >
                  {mapDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>

                {/* Zones/Layers Button - Below Map Theme */}
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 210,
                    right: 10,
                    zIndex: 1000,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                    color: 'text.primary',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(90, 90, 90, 0.98)' : 'rgba(240, 240, 240, 0.98)',
                    },
                  }}
                  onClick={() => setZoneDrawerOpen(true)}
                  title={t('zones.toggleVisibility', 'Toggle Zone Visibility')}
                >
                  <LayersIcon />
                </IconButton>

                <MapView
                  cpes={cpes}
                  mapDarkMode={mapDarkMode}
                  onCPEClick={handleCPEClick}
                  canAddCPE={canAddCPE}
                  mapControl={mapControlRef.current}
                  selectedCPEId={selectedCPEId}
                  onClearSelection={() => setSelectedCPEId(null)}
                  zones={zones}
                  visibleZones={visibleZones}
                  onCreated={() => {
                    loadCPEs();
                    loadZones();
                  }}
                />
              </Box>
            )}

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {t('map.showing', { count: cpes.length })}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* CPE Form Dialog */}
      <CPEFormDialog
        open={formDialogOpen}
        onClose={handleFormClose}
        initialLatitude={formInitialLat}
        initialLongitude={formInitialLng}
      />

      {/* CPE Details Dialog */}
      <CPEDetailsDialog
        open={detailsDialogOpen}
        onClose={() => {
          setDetailsDialogOpen(false);
          setSelectedCPEId(null);
          // Clear internal selection in map to revert pin to default state
          if (mapControlRef.current.deselectCPE) {
            mapControlRef.current.deselectCPE();
          }
        }}
        cpeId={selectedCPEId}
        onEdit={(id) => {
          // Navigate to edit page or open edit dialog
          navigate(`/cpes/${id}/edit`);
        }}
        onDelete={() => {
          // Reload map data after deletion
          loadCPEs();
        }}
      />

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">{t('filters.title')}</Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Filter */}
          <TextField
            fullWidth
            label={t('filters.searchPlaceholder')}
            placeholder={t('filters.searchPlaceholder')}
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Plant Type Filter - Multi-select */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.plantType')}</InputLabel>
            <Select
              multiple
              value={filters.plant_type || []}
              onChange={(e) => handleFilterChange('plant_type', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.plantType')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              {PLANT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  <Checkbox checked={(filters.plant_type || []).indexOf(type) > -1} />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* CPE Status Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.cpeStatus')}</InputLabel>
            <Select
              multiple
              value={filters.cpe_status || []}
              onChange={(e) => handleFilterChange('cpe_status', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.cpeStatus')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              {CPE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={(filters.cpe_status || []).indexOf(status) > -1} />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Commercial Status Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.commercialStatus')}</InputLabel>
            <Select
              multiple
              value={filters.commercial_status || []}
              onChange={(e) => handleFilterChange('commercial_status', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.commercialStatus')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                );
              }}
            >
              {COMMERCIAL_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox checked={(filters.commercial_status || []).indexOf(status) > -1} />
                  <ListItemText primary={status} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Internal Positioning Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.internalPositioning')}</InputLabel>
            <Select
              multiple
              value={filters.internal_positioning || []}
              onChange={(e) => handleFilterChange('internal_positioning', e.target.value)}
              input={<OutlinedInput label={t('cpe.fields.internalPositioning')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip
                        key={value}
                        label={value === 'go' ? t('cpe.positioning.go') : t('cpe.positioning.noGo')}
                        size="small"
                      />
                    ))}
                  </Box>
                );
              }}
            >
              <MenuItem value="go">
                <Checkbox checked={(filters.internal_positioning || []).indexOf('go') > -1} />
                <ListItemText primary={t('cpe.positioning.go')} />
              </MenuItem>
              <MenuItem value="no_go">
                <Checkbox checked={(filters.internal_positioning || []).indexOf('no_go') > -1} />
                <ListItemText primary={t('cpe.positioning.noGo')} />
              </MenuItem>
            </Select>
          </FormControl>

          {/* Waldevar PP Filter */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel shrink>{t('cpe.fields.isWaldevarPP', 'Waldevar PP')}</InputLabel>
            <Select
              value={filters.is_waldevar_pp !== undefined ? String(filters.is_waldevar_pp) : ''}
              onChange={(e) => {
                const val = e.target.value as string;
                handleFilterChange('is_waldevar_pp', val === '' ? undefined : val === 'true');
              }}
              displayEmpty
              input={<OutlinedInput label={t('cpe.fields.isWaldevarPP', 'Waldevar PP')} notched />}
            >
              <MenuItem value=""><em>{t('common.all')}</em></MenuItem>
              <MenuItem value="true">{t('common.yes')}</MenuItem>
              <MenuItem value="false">{t('common.no')}</MenuItem>
            </Select>
          </FormControl>

          {/* Power Range Filter */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label={t('filters.minPower')}
              type="number"
              value={filters.min_power || ''}
              onChange={(e) => handleFilterChange('min_power', e.target.value ? Number(e.target.value) : undefined)}
              fullWidth
            />
            <TextField
              label={t('filters.maxPower')}
              type="number"
              value={filters.max_power || ''}
              onChange={(e) => handleFilterChange('max_power', e.target.value ? Number(e.target.value) : undefined)}
              fullWidth
            />
          </Box>

          {/* Energization Year Filter */}
          <TextField
            fullWidth
            label={t('cpe.fields.energizationYear')}
            type="number"
            value={filters.energization_year || ''}
            onChange={(e) => handleFilterChange('energization_year', e.target.value ? Number(e.target.value) : undefined)}
            sx={{ mb: 2 }}
          />

          {/* Account Manager Filter */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel shrink>{t('cpe.fields.omAccountManager')}</InputLabel>
            <Select
              multiple
              value={filters.om_account_manager ? (Array.isArray(filters.om_account_manager) ? filters.om_account_manager : [filters.om_account_manager]) : []}
              onChange={(e) => {
                const selected = e.target.value as string[];
                handleFilterChange('om_account_manager', selected.length > 0 ? selected : undefined);
              }}
              input={<OutlinedInput label={t('cpe.fields.omAccountManager')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                const selectedIds = selected as string[];
                const selectedNames = selectedIds.map(id => users.find(u => u.id === id)?.name || users.find(u => u.id === id)?.email).filter(Boolean);
                return selectedNames.join(', ');
              }}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={(filters.om_account_manager as string[])?.includes(user.id) || false} />
                  <ListItemText primary={user.name || user.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Zone Filter */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel shrink>{t('cpe.fields.zone')}</InputLabel>
            <Select
              multiple
              value={filters.zone_id ? (Array.isArray(filters.zone_id) ? filters.zone_id : [filters.zone_id]) : []}
              onChange={(e) => {
                const selected = e.target.value as string[];
                handleFilterChange('zone_id', selected.length > 0 ? selected : undefined);
              }}
              input={<OutlinedInput label={t('cpe.fields.zone')} notched />}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || (selected as string[]).length === 0) {
                  return <em>{t('common.all')}</em>;
                }
                const selectedIds = selected as string[];
                const selectedNames = selectedIds.map(id => zones.find(z => z.id === id)?.name).filter(Boolean);
                return selectedNames.join(', ');
              }}
            >
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  <Checkbox checked={(filters.zone_id as string[])?.includes(zone.id) || false} />
                  <ListItemText primary={zone.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Next Action Filter */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel shrink>{t('nextAction.label', 'Next Action')}</InputLabel>
            <Select
              value={filters.next_action_id || ''}
              onChange={(e) => {
                const val = e.target.value || undefined;
                setFilters((prev) => ({ ...prev, next_action_id: val, overdue_next_action: undefined }));
              }}
              displayEmpty
              input={<OutlinedInput label={t('nextAction.label', 'Next Action')} notched />}
            >
              <MenuItem value=""><em>{t('nextAction.filter.all', 'All')}</em></MenuItem>
              <MenuItem value="none">{t('nextAction.filter.none', 'No action set')}</MenuItem>
              {nextActionOptions
                .filter((o) => o.is_active || o.id === filters.next_action_id)
                .map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Overdue Next Action toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Chip
              label={t('nextAction.filter.overdue', 'Overdue Only')}
              color={filters.overdue_next_action ? 'error' : 'default'}
              variant={filters.overdue_next_action ? 'filled' : 'outlined'}
              onClick={() => {
                handleFilterChange('overdue_next_action', filters.overdue_next_action ? undefined : true);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" fullWidth onClick={clearFilters}>
              {t('filters.clearFilters')}
            </Button>
            <Button variant="contained" fullWidth onClick={() => setFilterDrawerOpen(false)}>
              {t('common.close')}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Zone Visibility Drawer */}
      <Drawer
        anchor="right"
        open={zoneDrawerOpen}
        onClose={() => setZoneDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">{t('zones.title', 'Zones')}</Typography>
            <IconButton onClick={() => setZoneDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('zones.toggleDescription', 'Toggle zone visibility on the map')}
          </Typography>

          {/* Show All / Hide All Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={showAllZones}
            >
              {t('zones.showAll', 'Show All')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={hideAllZones}
            >
              {t('zones.hideAll', 'Hide All')}
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Zone List */}
          {zones.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('zones.noZones', 'No zones available')}
            </Typography>
          ) : (
            <List>
              {zones.map((zone) => (
                <ListItem
                  key={zone.id}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      checked={visibleZones.has(zone.id)}
                      onChange={() => toggleZoneVisibility(zone.id)}
                    />
                  }
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: zone.color,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                        <Typography variant="body2">{zone.name}</Typography>
                      </Box>
                    }
                    secondary={`${(zone.radius_meters / 1000).toFixed(1)} km radius`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Button
            variant="contained"
            fullWidth
            onClick={() => setZoneDrawerOpen(false)}
          >
            {t('common.close')}
          </Button>
        </Box>
      </Drawer>
      </Container>
  );
};

